// frontend/src/components/Dashboard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Ban, ArrowRight, Landmark, CreditCard, TrendingUp, GitPullRequestArrow, Sparkles } from 'lucide-react';
import api, { TokenStorage } from '../utils/api';
import { API_BASE } from '../utils/config';
import { useSync } from '../contexts/SyncContext';
import { useBusinessEntity } from '../contexts/BusinessEntityContext';
import AppShell from './layout/AppShell';
import GettingStartedChecklist from './common/GettingStartedChecklist';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtCurrency = (n) =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtCurrencyFull = (n) =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDay = (dateStr) => {
  const raw = String(dateStr);
  const d = raw.includes('T') ? new Date(raw) : new Date(raw + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso)) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

// Static icon map for account group types (avoids dynamic Tailwind class names)
const GROUP_CONFIG = {
  depository: {
    label: 'Checking & Savings',
    Icon: Landmark,
    iconClass: 'text-gray-400',
  },
  debt: {
    label: 'Credit Cards & Loans',
    Icon: CreditCard,
    iconClass: 'text-red-400',
  },
  investment: {
    label: 'Investments',
    Icon: TrendingUp,
    iconClass: 'text-blue-400',
  },
};

// Static alert badge styles (avoids dynamic Tailwind class names)
const ALERT_BADGE_STYLES = {
  match_pending: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dotBg: 'bg-amber-400',
  },
  suggestions_pending: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    dotBg: 'bg-blue-400',
  },
};

// ─── Skeleton (defined outside to avoid re-creation per render) ───────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-12 bg-gray-200 rounded-lg" />
      <div className="h-32 bg-gray-200 rounded-lg" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="h-20 bg-gray-200 rounded-lg" />
        <div className="h-20 bg-gray-200 rounded-lg" />
        <div className="h-20 bg-gray-200 rounded-lg" />
      </div>
    </div>
  );
}

// ─── Account grouping helper ──────────────────────────────────────────────────

function groupAccountsByEffectiveType(accounts) {
  const groups = {
    depository: [],
    debt: [],
    investment: [],
  };

  for (const acc of accounts) {
    const et = acc.effectiveType;
    if (et === 'credit' || et === 'loan') {
      groups.debt.push(acc);
    } else if (et === 'investment') {
      groups.investment.push(acc);
    } else {
      // Default: depository (checking, savings, etc.)
      groups.depository.push(acc);
    }
  }

  // Return only non-empty groups in display order
  const result = [];
  for (const key of ['depository', 'debt', 'investment']) {
    if (groups[key].length > 0) {
      const subtotal = groups[key].reduce((sum, a) => sum + (a.balance ?? 0), 0);
      result.push({ key, accounts: groups[key], subtotal });
    }
  }
  return result;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const useCookieFlow = (API_BASE || '').startsWith('http://localhost');
  const { lastSyncAt } = useSync();
  const { filterParam } = useBusinessEntity();

  const [profile, setProfile] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchDashboardData = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const params = new URLSearchParams();
      if (filterParam) params.append('businessEntityId', filterParam);
      const { data } = await api.get(`/api/dashboard/summary?${params.toString()}`);
      setSummaryData(data);
    } catch (err) {
      console.error('[Dashboard] summary fetch failed', err);
      setSummaryError('Could not load dashboard data.');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => { if (profile) fetchDashboardData(); }, [profile, filterParam]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastSyncAt && profile) fetchDashboardData(); }, [lastSyncAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = TokenStorage.getToken();
    if (!token) { navigate('/login', { replace: true }); return; }

    api.get('/api/user/me')
      .then(res => setProfile(res.data.user))
      .catch(err => { console.error(err); if (useCookieFlow) { handleRefreshToken(); } else { handleLogout(); } });

    if (useCookieFlow) {
      api.get('/api/auth/csrf-token', { withCredentials: true })
        .then(r => setCsrfToken(r.data?.csrfToken || ''))
        .catch(err => console.error('CSRF fetch failed:', err));
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefreshToken = async () => {
    if (!useCookieFlow) return;
    try {
      const res = await api.post('/api/auth/refresh', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true });
      TokenStorage.setToken(res.data.accessToken);
      const res2 = await api.get('/api/user/me');
      setProfile(res2.data.user);
    } catch (err) { console.error('Refresh failed:', err); handleLogout(); }
  };

  const handleLogout = async () => {
    try { if (useCookieFlow) { await api.post('/api/auth/logout', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true }); } }
    catch (err) { console.error('Logout error:', err); }
    finally { TokenStorage.removeToken(); navigate('/logged-out', { replace: true }); }
  };

  // Inline status update for Coming Up items
  async function handleStatusChange(instanceId, newStatus) {
    try {
      setUpdatingId(instanceId);
      await api.patch(`/api/forecast/${instanceId}/status`, { status: newStatus });
      await fetchDashboardData();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingId(null);
    }
  }

  const firstName = (profile?.first_name) || (profile?.given_name) || (typeof profile?.name === 'string' && profile.name.trim().length ? profile.name.trim().split(' ')[0] : null) || (typeof profile?.email === 'string' ? profile.email.split('@')[0] : '') || '';

  // Memoize account groups to avoid recalculating on every render
  const accountGroups = useMemo(
    () => groupAccountsByEffectiveType(summaryData?.accounts || []),
    [summaryData?.accounts]
  );

  // Filter actionable alerts (exclude overdraft_risk — shown in StatusBanner)
  const actionableAlerts = useMemo(
    () => (summaryData?.alerts || []).filter(
      a => a.type === 'match_pending' || a.type === 'suggestions_pending'
    ),
    [summaryData?.alerts]
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <AppShell onLogout={handleLogout}>
      {!profile ? (
        <DashboardSkeleton />
      ) : (
        <div className="max-w-5xl mx-auto space-y-4 sm:space-y-5">
          {/* 1. Welcome header */}
          <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">
            Welcome back, {firstName}
          </h2>

          {/* Loading / Error */}
          {summaryLoading && !summaryData && <DashboardSkeleton />}
          {summaryError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
              <p className="text-base sm:text-sm text-red-700">{summaryError}</p>
              <button onClick={fetchDashboardData} className="text-base sm:text-sm text-red-700 font-medium underline ml-4 min-h-[44px] sm:min-h-0">Retry</button>
            </div>
          )}

          {/* Onboarding checklist -- compact when mostly done */}
          <GettingStartedChecklist />

          {summaryData && (
            <>
              {/* 2. Status Banner + Per-Account Status List */}
              <StatusBanner data={summaryData} onNavigate={() => navigate('/planner/forecast')} />

              {(summaryData.accountStatuses || []).length > 1 && (
                <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                  {summaryData.accountStatuses.map(acct => {
                    const dotColor = {
                      danger: 'bg-red-500',
                      warning: 'bg-amber-500',
                      good: 'bg-emerald-500',
                    }[acct.variant] || 'bg-gray-400';

                    return (
                      <div key={acct.bankAccountId} className="flex items-center gap-3 px-4 py-2.5">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
                        <span className={`text-sm flex-1 truncate ${acct.variant === 'danger' ? 'font-semibold text-red-700' : 'text-gray-700'}`}>
                          {acct.accountName}
                        </span>
                        <span className={`text-xs flex-shrink-0 ${acct.variant === 'danger' ? 'text-red-600 font-medium' : acct.variant === 'warning' ? 'text-amber-600' : 'text-gray-400'}`}>
                          {acct.detail}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 3. Three Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg shadow p-4 border-l-4 border-l-emerald-500">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cash Available</p>
                  <p className="text-xl sm:text-2xl font-bold tabular-nums text-emerald-700 mt-1">
                    {fmtCurrencyFull(summaryData.totalCash)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border-l-4 border-l-red-500">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Owed</p>
                  <p className="text-xl sm:text-2xl font-bold tabular-nums text-red-600 mt-1">
                    {fmtCurrencyFull(summaryData.totalOwed)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4 border-l-4 border-l-blue-500">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Worth</p>
                  <p className="text-xl sm:text-2xl font-bold tabular-nums text-gray-800 mt-1">
                    {fmtCurrencyFull(summaryData.netWorth)}
                  </p>
                </div>
              </div>

              {/* 4. Account Grid Grouped by Effective Type */}
              {accountGroups.length > 0 && (
                <div className="space-y-4">
                  {accountGroups.map((group, groupIdx) => {
                    const cfg = GROUP_CONFIG[group.key];
                    const GroupIcon = cfg.Icon;

                    return (
                      <div key={group.key}>
                        {/* Group header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <GroupIcon className={`w-4 h-4 ${cfg.iconClass}`} />
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                              {cfg.label}
                            </h3>
                            <span className="text-sm font-semibold tabular-nums text-gray-600">
                              {fmtCurrencyFull(group.subtotal)}
                            </span>
                          </div>
                          {groupIdx === 0 && summaryData.lastSyncAt && (
                            <span className="text-xs text-gray-400">
                              Last sync: {timeAgo(summaryData.lastSyncAt)}
                            </span>
                          )}
                        </div>

                        {/* Account cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                          {group.accounts.map(acc => (
                            <AccountCard key={acc.bankAccountId} account={acc} groupKey={group.key} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 5. Pending Actions */}
              {actionableAlerts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {actionableAlerts.map((alert, idx) => {
                    const style = ALERT_BADGE_STYLES[alert.type] || ALERT_BADGE_STYLES.match_pending;
                    const AlertIcon = alert.type === 'suggestions_pending' ? Sparkles : GitPullRequestArrow;
                    const linkTo = alert.type === 'suggestions_pending'
                      ? '/planner/subscriptions'
                      : '/planner/matching';

                    return (
                      <button
                        key={idx}
                        onClick={() => navigate(linkTo)}
                        className={`inline-flex items-center gap-2 px-3 py-2 sm:py-1.5 rounded-full text-sm font-medium border min-h-[44px] sm:min-h-0 ${style.bg} ${style.border} ${style.text} hover:opacity-80 transition-opacity`}
                      >
                        <span className={`w-2 h-2 rounded-full ${style.dotBg}`} />
                        <AlertIcon className="w-3.5 h-3.5" />
                        <span>{alert.count ?? ''} {alert.label}</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* 6. Coming Up */}
              <div className="bg-white rounded-lg shadow">
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Coming Up</h3>
                    {summaryData.weekSummary && (summaryData.weekSummary.totalIn > 0 || summaryData.weekSummary.totalOut > 0) && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        This week: {summaryData.weekSummary.totalIn > 0 && (
                          <span className="text-emerald-600 font-medium">{fmtCurrency(summaryData.weekSummary.totalIn)} in</span>
                        )}
                        {summaryData.weekSummary.totalIn > 0 && summaryData.weekSummary.totalOut > 0 && ', '}
                        {summaryData.weekSummary.totalOut > 0 && (
                          <span className="text-red-600 font-medium">{fmtCurrency(summaryData.weekSummary.totalOut)} out</span>
                        )}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate('/planner/forecast')}
                    className="text-sm sm:text-xs text-blue-600 hover:underline flex-shrink-0 min-h-[44px] sm:min-h-0 inline-flex items-center gap-1"
                  >
                    View Forecast <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="divide-y divide-gray-100">
                  {(summaryData.comingUp || []).length === 0 ? (
                    <p className="text-base sm:text-sm text-gray-400 italic px-4 py-6">Nothing scheduled this week</p>
                  ) : (
                    (summaryData.comingUp || []).map(item => {
                      const isIncome = item.itemType === 'income';
                      const isDone = item.status === 'paid' || item.status === 'cleared' || item.status === 'skipped';
                      const isUpdating = updatingId === item.id;

                      return (
                        <div
                          key={item.id}
                          className={`flex items-center gap-2 sm:gap-3 px-4 py-3 ${
                            item.isToday && !isDone ? 'border-l-4 border-red-400 pl-3' : ''
                          } ${isDone ? 'opacity-50' : ''}`}
                        >
                          <span className={`text-xs w-20 sm:w-24 flex-shrink-0 ${item.isToday ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                            {fmtDay(item.date)}
                          </span>
                          <span className="flex-1 text-sm text-gray-800 truncate">{item.name}</span>
                          <span className={`text-sm tabular-nums font-medium flex-shrink-0 ${isIncome ? 'text-emerald-600' : 'text-gray-700'}`}>
                            {isIncome ? '+' : '-'}{fmtCurrency(item.amount)}
                          </span>
                          {!isDone && !isUpdating && (
                            <div className="flex-shrink-0 flex gap-1">
                              <button
                                onClick={() => handleStatusChange(item.id, 'paid')}
                                className="p-2 rounded text-emerald-600 hover:bg-emerald-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                                aria-label="Mark paid"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleStatusChange(item.id, 'skipped')}
                                className="p-2 rounded text-gray-400 hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                                aria-label="Skip"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          {isUpdating && (
                            <span className="text-xs text-gray-400 flex-shrink-0">...</span>
                          )}
                          {isDone && (
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              {item.status === 'paid' || item.status === 'cleared' ? '\u2713' : '\u2298'}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </AppShell>
  );
}

// ─── Account Card ─────────────────────────────────────────────────────────────

function AccountCard({ account, groupKey }) {
  const acc = account;

  if (groupKey === 'debt') {
    return (
      <div className="bg-red-50 rounded-lg shadow p-3 border border-red-200">
        <div className="flex items-center gap-1.5 mb-1">
          <CreditCard className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <p className="text-xs text-gray-500 truncate">{acc.institutionName}</p>
        </div>
        <p className="text-sm font-medium text-gray-800 truncate">{acc.accountName}</p>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-red-500 mt-1">
          Owed
        </p>
        <p className="text-sm font-semibold tabular-nums text-red-700">
          {fmtCurrencyFull(acc.balance)}
        </p>
        {acc.limit != null && (
          <p className="text-xs text-gray-500 mt-0.5">
            Credit limit: {fmtCurrency(acc.limit)}
          </p>
        )}
      </div>
    );
  }

  if (groupKey === 'investment') {
    return (
      <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
        <div className="flex items-center gap-1.5 mb-1">
          <TrendingUp className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
          <p className="text-xs text-gray-400 truncate">{acc.institutionName}</p>
        </div>
        <p className="text-sm font-medium text-gray-800 truncate">{acc.accountName}</p>
        <p className="text-sm font-semibold tabular-nums mt-0.5 text-emerald-700">
          {fmtCurrencyFull(acc.balance)}
        </p>
      </div>
    );
  }

  // Default: depository (checking/savings)
  return (
    <div className="bg-white rounded-lg shadow p-3 border border-gray-100">
      <div className="flex items-center gap-1.5 mb-1">
        <Landmark className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <p className="text-xs text-gray-400 truncate">{acc.institutionName}</p>
      </div>
      <p className="text-sm font-medium text-gray-800 truncate">{acc.accountName}</p>
      <p className="text-sm font-semibold tabular-nums mt-0.5 text-emerald-700">
        {fmtCurrencyFull(acc.balance)}
      </p>
      {acc.available != null && (
        <p className="text-xs text-gray-400 mt-0.5">
          Available: {fmtCurrencyFull(acc.available)}
        </p>
      )}
    </div>
  );
}

// ─── Status Banner ────────────────────────────────────────────────────────────

function StatusBanner({ data, onNavigate }) {
  const config = {
    good:    { border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-800', icon: '\u2713' },
    warning: { border: 'border-l-amber-500',   bg: 'bg-amber-50',  text: 'text-amber-800',   icon: '\u26A0' },
    danger:  { border: 'border-l-red-500',     bg: 'bg-red-50',    text: 'text-red-800',      icon: '\u26A0' },
  }[data.statusVariant || 'good'];

  return (
    <div className={`flex items-center justify-between rounded-lg border-l-4 ${config.border} ${config.bg} px-4 py-3`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`font-medium ${config.text}`} aria-hidden="true">{config.icon}</span>
        <p className={`text-sm font-medium ${config.text} truncate`}>
          {data.statusDetail || data.statusLabel}
        </p>
      </div>
      <button
        onClick={onNavigate}
        className="text-sm sm:text-xs text-blue-600 hover:underline flex-shrink-0 ml-3 min-h-[44px] sm:min-h-0 inline-flex items-center gap-1"
      >
        Forecast <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
